import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useSubscription, TIERS } from '@/features/subscription/hooks/useSubscription';

export function useContractQuota() {
  const { session } = useAuth();
  const { tier, isInTrial } = useSubscription();
  const limit = TIERS[tier].monthlyContracts;
  const isFree = tier === 'free' && !isInTrial;

  const { data: used = 0, isLoading } = useQuery({
    queryKey: ['contract-quota', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return 0;
      const start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      const { count, error } = await supabase
        .from('signed_contracts')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', session.user.id)
        .gte('signed_at', start.toISOString());
      if (error) return 0;
      return count ?? 0;
    },
    enabled: !!session?.user?.id && isFree,
    staleTime: 30_000,
  });

  const effectiveUsed = isFree ? used : 0;
  const remaining = Number.isFinite(limit) ? Math.max(0, limit - effectiveUsed) : Infinity;

  return {
    used: effectiveUsed,
    limit,
    remaining,
    hasReachedLimit: Number.isFinite(limit) && effectiveUsed >= limit,
    isNearLimit: Number.isFinite(limit) && remaining <= 3 && remaining > 0,
    isLoading: isFree && isLoading,
  };
}
